import { payBoxTypes, saleItemUtils } from '@/mixins'
import isElectron from 'is-electron'
import axiosInstance from '@/libs/axios'
import catalog from './atm-catalog'
import status from './atm-status'

export const atmScreenEnum = {
  CATALOG: 'catalog',
  PAYMENT: 'payment',
}

const getInitialBaseData = () => ({
  bannersAtm: [],
})

const getInitialState = () => ({
  baseData: getInitialBaseData(),
  cartValidationResult: null,
})

export default {
  namespaced: true,
  state: getInitialState(),
  modules: {
    catalog,
    status,
  },

  getters: {
    getBannersAtm(state) {
      return state.baseData.bannersAtm || []
    },

    getDictCartItemsWithErrors(state) {
      return (state.cartValidationResult?.items || []).reduce((dict, item) => {
        const stockAvailable = Math.floor(item.stockAvailable || 0)
        return {
          ...dict,
          [item.index]: {
            ...item,
            stockAvailable: stockAvailable > 0 ? stockAvailable : 0,
          }
        }
      }, {})
    },
    isCartWithErrors(state) {
      return state.cartValidationResult?.items?.length > 0
    }
  },

  mutations: {
    SET_BANNERS_ATM(state, banners) {
      state.baseData.bannersAtm = banners
    },
    SET_CART_VALIDATION_RESULT(state, cartValidationResult) {
      state.cartValidationResult = cartValidationResult
    },

    CLEAN_BASE_DATA(state) {
      state.baseData = getInitialBaseData()
    },
    CLEAN_STATE(state) {
      const defaultData = getInitialState()

      const cleanIgnore = ['baseData']
      Object.keys(defaultData).forEach(key => {
        if (cleanIgnore.includes(key)) return
        state[key] = defaultData[key]
      })
    },
  },

  actions: {
    /**
     * Inicializa os dados base do ATM (status, métodos de pagamento, banners)
     * Deve ser chamado uma vez ao inicializar o PDV ATM
     */
    async initAtmBase({ dispatch }) {
      await dispatch('pages/pdv/atm/status/loadAtmStatus', {}, { root: true })
      await dispatch('pages/pdv/loadAtmPaymentMethods', {}, { root: true })
      await dispatch('fetchBannersAtm')
    },

    /**
     * Carrega o catálogo de produtos do ATM
     * Deve ser chamado ao entrar na tela do catálogo (pdv-atm)
     */
    async loadAtmCatalogData({ dispatch }) {
      await dispatch('pages/pdv/atm/catalog/loadAtmCatalog', {}, { root: true })
    },

    /**
     * @deprecated Use initAtmBase + loadAtmCatalogData separadamente
     * Mantido para compatibilidade
     */
    async initAtm({ dispatch }) {
      await dispatch('initAtmBase')
      await dispatch('loadAtmCatalogData')
    },

    async fetchBannersAtm({ state, commit }) {
      if (!isElectron()) return

      if (state.baseData.bannersAtm.length > 0) {
        // Já carregou os banners
        console.info('ATM banners already loaded, skipping...')
        return
      }

      try {
        // Não pega os dados do state pois no login ainda não existe a info.
        const payBoxData = await window.electronAPI.system.identifyPayboxRead()
        const systemInfo = await window.electronAPI.system.systemInfo()

        const payBoxIdentity = payBoxData?.guid || systemInfo?.macAddress || ''
        const { data } = await axiosInstance.get(
          `/api/sales/pay-box-banner/atm/${payBoxIdentity}`,
          {
            params: { pageSize: 3 },
          }
        )

        commit('SET_BANNERS_ATM', data.results || [])
      } catch (error) {
        console.error('[Atm] fetchBannersAtm', error)
      }
    },

    async validateCartItems({ rootGetters, commit }) {
      commit('SET_CART_VALIDATION_RESULT', null)
      const { store: payBoxStore, eventId } =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const cartItems = rootGetters['pages/pdv/payBox/getItems']

      const itensToValidate = cartItems
        .flatMap(item => {
          if (item.kitItems?.length > 0) return item.kitItems

          return [item]
        })
        .flatMap(item => {
          if (item.lendingProducts?.length > 0) return [item, ...item.lendingProducts]

          return [item]
        })
        .map(item => {
          const { priceRuleSelected } = item
          const { productKit, promotion } = priceRuleSelected

          return {
            index: item.localId,
            skuId: item.skuProduct.skuId,
            productKitId: productKit?.id,
            promotionId: promotion?.id,
            quantity: item.quantity,
          }
        })

      const { data } = await axiosInstance.post(
        `/api/sales/pay-box-sale/atm/validate-cart-items`,
        {
          storeId: payBoxStore.id,
          eventId,
          items: itensToValidate,
        }
      )

      commit('SET_CART_VALIDATION_RESULT', data)

      const hasExceedStock = data?.items?.some(i => i.exceedStock)
      return {
        isValid: !hasExceedStock,
        hasExceedStock,
      }
    },

    async addItemFromAtm({ dispatch }, { amount, ean, skuId, isKit, isSuggestion }) {
      try {
        await dispatch('pages/pdv/atm/status/addPendingAction', 'addItem', { root: true })
        const product = await dispatch(
          'pages/pdv/atm/catalog/fetchProductToCart',
          { ean, skuId, isKit },
          { root: true }
        )

        const cartSaleItem = saleItemUtils.buildProductToSaleItem(product, {
          quantity: amount,
          isSuggestion,
          inputMethod: payBoxTypes.data().productInputMethodEnum.TYPED,
        })

        await dispatch(
          'pages/pdv/payBox/addItem',
          {
            item: cartSaleItem,
            useStockAsMax: true,
          },
          { root: true }
        )
      } finally {
        await dispatch('pages/pdv/atm/status/removePendingAction', 'addItem', { root: true })
      }
    },
    async removeItemFromAtm({ dispatch }, item) {
      try {
        await dispatch('pages/pdv/atm/status/addPendingAction', 'removeItem', { root: true })

        await dispatch('pages/pdv/payBox/removeItem', item, { root: true })
      } finally {
        await dispatch('pages/pdv/atm/status/removePendingAction', 'removeItem', { root: true })
      }
    },
    async updateItemFromAtm({ dispatch }, { item, amount }) {
      try {
        await dispatch('pages/pdv/atm/status/addPendingAction', 'updateItem', { root: true })

        await dispatch(
          'pages/pdv/payBox/updateItem',
          { payload: { ...item, quantity: amount }, useStockAsMax: true },
          { root: true }
        )
      } finally {
        await dispatch('pages/pdv/atm/status/removePendingAction', 'updateItem', { root: true })
      }
    },

    async secureCleanAtmState({ dispatch, rootGetters }) {
      const onlySaleCancelShouldCleanState = rootGetters['pages/pdv/payBox/onlySaleCancelShouldCleanState']

      if (onlySaleCancelShouldCleanState) {
        return
      }

      // Após reiniciar o app o Vuex está vazio, mas a venda paga pode estar no IndexedDB.
      // A restauração fica com o loadPdvData.
      if (await dispatch('pages/pdv/payBox/checkHasLastSessionWithPayments', null, { root: true })) {
        return
      }

      await dispatch('cleanState')
    },

    async cleanState({ commit, dispatch }, { cleanAll } = { cleanAll: false }) {
      await dispatch('pages/pdv/payBox/cleanState', {}, { root: true })
      await dispatch('pages/pdv/atm/status/cleanState', {}, { root: true })
      await dispatch('pages/pdv/payBoxInvoice/cleanPayBoxInvoiceMetadata', {}, { root: true })
      commit('CLEAN_STATE')

      if (cleanAll) {
        commit('CLEAN_BASE_DATA')
      }
    },
  },
}
